/**
 * 상담 기록 생성/수정 모달
 * Counseling Record Form Modal
 */

import React, { useState, useEffect } from 'react';
import { counselingRecordService } from '../services/CounselingRecordService';
import {
  CounselingRecord,
  CounselingRecordCreate,
  CounselingRecordUpdate,
  CounselingType,
  CounselingTypeLabels,
  validateCounselingRecord,
  StudentDailyRecord,
  FocusMetric,
  StudyGoal,
  PreConsultationMetrics,
} from '../models/CounselingRecord';
import { StudentLearningDataSummary } from './StudentLearningDataSummary';
import { StudentStudySummary } from '../../../components/learning/StudentStudySummary';
import './CounselingRecordFormModal.css';
import { getTodayKST } from '../../../utils/dateUtils';

interface CounselingRecordFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (record: CounselingRecord) => void;
  studentId: string;
  studentName?: string;
  editRecord?: CounselingRecord;
}

export const CounselingRecordFormModal: React.FC<CounselingRecordFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  studentId,
  studentName,
  editRecord,
}) => {
  const [formData, setFormData] = useState<CounselingRecordCreate>({
    student_id: studentId,
    counseling_date: getTodayKST(),
    counseling_type: 'regular',
    topic: '',
    content: '',
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // 학습 데이터 상태 (수정 모드에서는 기존 데이터 표시, 생성 모드에서는 불러오기)
  const [learningData, setLearningData] = useState<{
    dailyRecords?: StudentDailyRecord[];
    focusMetrics?: FocusMetric[];
    studyGoals?: StudyGoal[];
    preConsultationMetrics?: PreConsultationMetrics;
  } | null>(null);
  const [showLearningData, setShowLearningData] = useState<boolean>(false);


  useEffect(() => {
    if (editRecord) {
      // 수정 모드: 기존 데이터 로드
      setFormData({
        student_id: editRecord.student_id,
        counseling_date: editRecord.counseling_date,
        counseling_type: editRecord.counseling_type,
        duration_minutes: editRecord.duration_minutes,
        topic: editRecord.topic,
        content: editRecord.content,
        student_feedback: editRecord.student_feedback,
        growth_score: editRecord.growth_score,
        attitude_score: editRecord.attitude_score,
        concentration_score: editRecord.concentration_score,
        goal_achievement_score: editRecord.goal_achievement_score,
        current_issues: editRecord.current_issues,
        action_plan: editRecord.action_plan,
        next_goal: editRecord.next_goal,
        next_counseling_date: editRecord.next_counseling_date,
        visible_to_student: editRecord.visible_to_student,
        visible_to_parent: editRecord.visible_to_parent,
      });

      // 수정 모드: 기존 학습 데이터 표시 (이미 저장된 데이터)
      if (editRecord.related_daily_records || editRecord.related_focus_metrics || editRecord.related_study_goals) {
        setLearningData({
          dailyRecords: editRecord.related_daily_records,
          focusMetrics: editRecord.related_focus_metrics,
          studyGoals: editRecord.related_study_goals,
          preConsultationMetrics: editRecord.pre_consultation_metrics,
        });
        setShowLearningData(true);
      }
    } else {
      // 생성 모드: 초기화
      setFormData({
        student_id: studentId,
        counseling_date: getTodayKST(),
        counseling_type: 'regular',
        topic: '',
        content: '',
      });
      setLearningData(null);
      setShowLearningData(false);
    }
    setErrors([]);
  }, [isOpen, editRecord, studentId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      const numValue = value === '' ? undefined : parseInt(value, 10);
      setFormData((prev) => ({ ...prev, [name]: numValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    const validation = validateCounselingRecord(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      let record: CounselingRecord;
      if (editRecord) {
        // 수정
        const updateData: CounselingRecordUpdate = {
          counseling_date: formData.counseling_date,
          counseling_type: formData.counseling_type,
          duration_minutes: formData.duration_minutes,
          topic: formData.topic,
          content: formData.content,
          student_feedback: formData.student_feedback,
          growth_score: formData.growth_score,
          attitude_score: formData.attitude_score,
          concentration_score: formData.concentration_score,
          goal_achievement_score: formData.goal_achievement_score,
          current_issues: formData.current_issues,
          action_plan: formData.action_plan,
          next_goal: formData.next_goal,
          next_counseling_date: formData.next_counseling_date,
          visible_to_student: formData.visible_to_student,
          visible_to_parent: formData.visible_to_parent,
        };
        record = await counselingRecordService.updateCounselingRecord(editRecord.id, updateData);
      } else {
        // 생성
        record = await counselingRecordService.createCounselingRecord(formData);
      }
      onSuccess(record);
      onClose();
    } catch (err) {
      setErrors([err instanceof Error ? err.message : '저장에 실패했습니다.']);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="counseling-form-modal-overlay" onClick={onClose}>
      <div className="counseling-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editRecord ? '상담 기록 수정' : '새 상담 기록'}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="counseling-form">
          {errors.length > 0 && (
            <div className="form-errors">
              {errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}

          <div className="form-section">
            <h3>기본 정보</h3>

            {studentName && (
              <div className="form-group">
                <label>학생</label>
                <input type="text" value={studentName} disabled />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="counseling_date">상담 일자 *</label>
                <input
                  type="date"
                  id="counseling_date"
                  name="counseling_date"
                  value={formData.counseling_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="counseling_type">상담 유형 *</label>
                <select
                  id="counseling_type"
                  name="counseling_type"
                  value={formData.counseling_type}
                  onChange={handleChange}
                  required
                >
                  {Object.entries(CounselingTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="duration_minutes">상담 시간 (분)</label>
                <input
                  type="number"
                  id="duration_minutes"
                  name="duration_minutes"
                  value={formData.duration_minutes || ''}
                  onChange={handleChange}
                  min="1"
                  max="300"
                />
              </div>
            </div>
          </div>

          {/* 학습 데이터 요약 (신규 컴포넌트 - 상담 작성 시 항상 표시) */}
          {!editRecord && (
            <div className="form-section">
              <StudentStudySummary
                studentId={studentId}
                studentName={studentName}
                days={14}
                collapsed={false}
              />
            </div>
          )}

          {/* 기존 학습 데이터 섹션 (수정 모드 또는 상세 데이터 필요 시) */}
          {!editRecord && showLearningData && learningData && (
            <div className="form-section learning-data-section">
              <div className="section-header">
                <h3>📊 상세 학습 데이터</h3>
                <button
                  type="button"
                  className="btn-toggle-learning-data"
                  onClick={() => setShowLearningData(false)}
                >
                  숨기기
                </button>
              </div>
              <StudentLearningDataSummary
                dailyRecords={learningData.dailyRecords}
                focusMetrics={learningData.focusMetrics}
                studyGoals={learningData.studyGoals}
                preConsultationMetrics={learningData.preConsultationMetrics}
              />
            </div>
          )}

          {/* 수정 모드에서 학습 데이터 표시 */}
          {editRecord && learningData && (
            <div className="form-section learning-data-section">
              <h3>📊 저장된 학습 데이터</h3>
              <p className="hint-text">상담 작성 시점에 수집된 학습 데이터입니다.</p>
              <StudentLearningDataSummary
                dailyRecords={learningData.dailyRecords}
                focusMetrics={learningData.focusMetrics}
                studyGoals={learningData.studyGoals}
                preConsultationMetrics={learningData.preConsultationMetrics}
              />
            </div>
          )}

          <div className="form-section">
            <h3>상담 내용</h3>

            <div className="form-group">
              <label htmlFor="topic">상담 주제 *</label>
              <input
                type="text"
                id="topic"
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                placeholder="예: 학습 태도 개선 상담"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="content">상담 내용 *</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows={8}
                placeholder="상담 내용을 상세히 기록해주세요..."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="student_feedback">학생 피드백</label>
              <textarea
                id="student_feedback"
                name="student_feedback"
                value={formData.student_feedback || ''}
                onChange={handleChange}
                rows={3}
                placeholder="학생의 의견이나 피드백을 기록해주세요..."
              />
            </div>
          </div>

          <div className="form-section">
            <h3>평가 (1-5점)</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="growth_score">성장</label>
                <input
                  type="number"
                  id="growth_score"
                  name="growth_score"
                  value={formData.growth_score || ''}
                  onChange={handleChange}
                  min="1"
                  max="5"
                />
              </div>

              <div className="form-group">
                <label htmlFor="attitude_score">태도</label>
                <input
                  type="number"
                  id="attitude_score"
                  name="attitude_score"
                  value={formData.attitude_score || ''}
                  onChange={handleChange}
                  min="1"
                  max="5"
                />
              </div>

              <div className="form-group">
                <label htmlFor="concentration_score">집중력</label>
                <input
                  type="number"
                  id="concentration_score"
                  name="concentration_score"
                  value={formData.concentration_score || ''}
                  onChange={handleChange}
                  min="1"
                  max="5"
                />
              </div>

              <div className="form-group">
                <label htmlFor="goal_achievement_score">목표달성</label>
                <input
                  type="number"
                  id="goal_achievement_score"
                  name="goal_achievement_score"
                  value={formData.goal_achievement_score || ''}
                  onChange={handleChange}
                  min="1"
                  max="5"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>목표 및 계획</h3>

            <div className="form-group">
              <label htmlFor="current_issues">현재 문제점</label>
              <textarea
                id="current_issues"
                name="current_issues"
                value={formData.current_issues || ''}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="action_plan">실행 계획</label>
              <textarea
                id="action_plan"
                name="action_plan"
                value={formData.action_plan || ''}
                onChange={handleChange}
                rows={4}
                placeholder="구체적인 실행 계획을 작성해주세요..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="next_goal">다음 목표</label>
              <textarea
                id="next_goal"
                name="next_goal"
                value={formData.next_goal || ''}
                onChange={handleChange}
                rows={3}
                placeholder="다음 상담까지의 목표를 설정해주세요..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="next_counseling_date">다음 상담 예정일</label>
              <input
                type="date"
                id="next_counseling_date"
                name="next_counseling_date"
                value={formData.next_counseling_date || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>공개 설정</h3>

            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="visible_to_student"
                  checked={formData.visible_to_student ?? true}
                  onChange={handleChange}
                />
                학생 열람 가능
              </label>
            </div>

            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="visible_to_parent"
                  checked={formData.visible_to_parent ?? true}
                  onChange={handleChange}
                />
                학부모 열람 가능
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
              취소
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? '저장 중...' : editRecord ? '수정' : '등록'}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default CounselingRecordFormModal;
